import React, { useState, useEffect, useContext } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUsers, 
    faPlus, 
    faUserMd,
    faCrown,
    faUserMinus,
    faSearch,
    faEllipsisVertical,
    faUserPlus,
    faUserCheck,
    faExchangeAlt,
    faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './header';
import Footer from './footer';
import { useCustomAlert } from './CustomAlert';
import './GroupMembers.css';

const GroupMembers = () => {
    const { user } = useContext(MyContext);
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { showAlert, AlertComponent } = useCustomAlert();
    
    const [group, setGroup] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState('member');
    const [isGroupCreator, setIsGroupCreator] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [followStatus, setFollowStatus] = useState({});

    useEffect(() => {
        if (user && user.user_id && groupId) {
            loadGroupData();
            loadMembers();
        }
    }, [user, groupId]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeDropdown && !event.target.closest('.dropdown')) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeDropdown]);

    const loadGroupData = async () => {
        try {
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    status: 'active'
                })
            });

            const data = await response.json();
            if (data.success) {
                const currentGroup = data.data.find(g => g.group_id == groupId);
                if (currentGroup) {
                    setGroup(currentGroup);
                    setUserRole(currentGroup.role);
                    
                    // Check if current user is the actual group creator
                    // Since created_by ID is not available, we'll use the creator username
                    const userId = parseInt(user.user_id);
                    const isActualOwner = currentGroup.creator_username === user.username;
                    
                    // For UI purposes, admins can manage the group
                    let isCreator = isActualOwner;
                    if (!isCreator && currentGroup.role === 'admin') {
                        console.log('Fallback: User is admin, assuming they are creator for UI purposes only');
                        isCreator = true;
                    }
                    
                    console.log('GroupMembers - Creator detection:', {
                        userId: userId,
                        userRole: currentGroup.role,
                        creatorUsername: currentGroup.creator_username,
                        currentUserUsername: user.username,
                        isCreator: isCreator,
                        isActualOwner: isActualOwner,
                        allGroupFields: Object.keys(currentGroup)
                    });
                    
                    setIsGroupCreator(isCreator);
                    // Store actual owner status for transfer ownership logic
                    setGroup(prev => ({ ...prev, isActualOwner }));
                } else {
                    setError('Group not found or access denied');
                }
            }
        } catch (err) {
            setError('Failed to load group data');
            console.error('Error loading group data:', err);
        }
    };

    const loadMembers = async () => {
        try {
            setLoading(true);
            
            const response = await fetch(`${backendUrl}/get_group_members.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id,
                    status: 'active'
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            console.log('Load members response:', data); // Debug log
            
            if (data.success) {
                // The API returns data in this structure: { data: { members: [...], statistics: {...} } }
                let membersData = [];
                if (data.data && data.data.members && Array.isArray(data.data.members)) {
                    membersData = data.data.members;
                } else if (Array.isArray(data.data)) {
                    // Fallback for direct array response
                    membersData = data.data;
                } else {
                    console.error('Unexpected API response structure:', data);
                    setError('Unexpected response format from server');
                    return;
                }
                
                console.log('Processed members data:', membersData); // Debug log
                setMembers(membersData);
            } else {
                setError(data.messages || 'Failed to load members');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error loading members:', err);
        } finally {
            setLoading(false);
        }
    };

    const removeGroupMember = async (memberToRemove) => {
        showAlert('warning', 'Confirm Removal', `Are you sure you want to remove ${memberToRemove.first_name} ${memberToRemove.last_name} from this group?`, 0);
        // Note: In a real implementation, you'd want to handle the confirmation differently
        // For now, we'll proceed with the removal
        
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(`${backendUrl}/remove_group_member.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id_to_remove: memberToRemove.user_id,
                    removed_by: user.user_id
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                loadMembers();
            } else {
                setError(data.messages || 'Failed to remove member');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error removing member:', err);
        } finally {
            setLoading(false);
        }
    };

    const toggleFollow = async (member) => {
        try {
            const isFollowing = followStatus[member.user_id];
            const newStatus = !isFollowing;
            
            // Update local state immediately for better UX
            setFollowStatus(prev => ({
                ...prev,
                [member.user_id]: newStatus
            }));
            
            // Here you would make an API call to follow/unfollow
            // For now, we'll just update the local state
            console.log(`${newStatus ? 'Following' : 'Unfollowing'} ${member.username}`);
            
        } catch (err) {
            // Revert on error
            setFollowStatus(prev => ({
                ...prev,
                [member.user_id]: !prev[member.user_id]
            }));
            console.error('Error toggling follow status:', err);
        }
    };

    const updateMemberRole = async (member, newRole) => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(`${backendUrl}/update_group_member_role.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: member.user_id,
                    new_role: newRole,
                    updated_by: user.user_id
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                loadMembers();
                setActiveDropdown(null);
            } else {
                setError(data.messages || 'Failed to update member role');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error updating member role:', err);
        } finally {
            setLoading(false);
        }
    };

    const transferOwnership = async (member) => {
        showAlert('warning', 'Transfer Ownership', `Are you sure you want to transfer group ownership to ${member.first_name} ${member.last_name}? This action cannot be undone.`, 0);
        
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(`${backendUrl}/transfer_group_ownership.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    new_owner_id: member.user_id,
                    current_owner_id: user.user_id
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                loadMembers();
                setActiveDropdown(null);
            } else {
                setError(data.messages || 'Failed to transfer ownership');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error transferring ownership:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredMembers = members.filter(member => {
        const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
        const username = member.username.toLowerCase();
        const search = searchTerm.toLowerCase();
        return fullName.includes(search) || username.includes(search);
    });

    const canManageMembers = userRole === 'admin' || isGroupCreator;

    if (loading && !group) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading members...</div>
            </div>
        );
    }

    if (error && !group) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 d-flex flex-column">
            <Header />
            
            <div className="container-fluid p-3 flex-grow-1" style={{ marginTop: '80px', marginBottom: '60px' }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="mb-0">
                            <FontAwesomeIcon icon={faUsers} className="me-2" />
                            Members
                        </h2>
                        <small className="text-muted">{group?.group_name}</small>
                    </div>
                    {canManageMembers && (
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowAddModal(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Add Member
                        </button>
                    )}
                </div>

                {/* Search */}
                <div className="search-container">
                    <div className="input-group">
                        <span className="input-group-text">
                            <FontAwesomeIcon icon={faSearch} />
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search members..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {/* Members List */}
                {loading ? (
                    <div className="d-flex justify-content-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : filteredMembers.length === 0 ? (
                    <div className="text-center py-5">
                        <FontAwesomeIcon icon={faUsers} size="3x" className="text-muted mb-3" />
                        <h5 className="text-muted">No members found</h5>
                        <p className="text-muted">
                            {searchTerm 
                                ? 'Try adjusting your search criteria'
                                : 'No members in this group'
                            }
                        </p>
                    </div>
                ) : (
                    <div className="members-list">
                        {filteredMembers.map((member) => {
                            // Check if this member is the group owner using creator username
                            const memberIsOwner = member.username === group?.creator_username;
                            
                            const canRemoveMember = canManageMembers && 
                                                   member.user_id !== user.user_id && 
                                                   !memberIsOwner && // Cannot remove group owner
                                                   (isGroupCreator || member.role !== 'admin');
                            const canPromoteToAdmin = canManageMembers && 
                                                     member.user_id !== user.user_id && 
                                                     member.role === 'member';
                            const canTransferOwnership = group?.isActualOwner && 
                                                       member.user_id !== user.user_id;
                            const isFollowing = followStatus[member.user_id] || false;
                            const isCurrentUser = member.user_id === user.user_id;
                            
                            // Debug logging
                            console.log('Member:', member.first_name, 'Username:', member.username, 'Group Creator Username:', group?.creator_username, 'Is Group Creator:', isGroupCreator, 'Is Actual Owner:', group?.isActualOwner, 'Member Is Owner:', memberIsOwner, 'Can Remove:', canRemoveMember, 'Can Transfer:', canTransferOwnership);
                            
                            return (
                                <div key={member.user_id} className="member-card">
                                    <div className="member-photo">
                                        {member.photo ? (
                                            <img 
                                                src={member.photo} 
                                                alt={member.first_name}
                                                className="rounded-circle"
                                            />
                                        ) : (
                                            <div className="default-photo">
                                                {member.first_name ? member.first_name.charAt(0).toUpperCase() : '?'}
                                            </div>
                                        )}
                                    </div>
                                    <div className="member-info">
                                        <div className="member-name">
                                            <h6 className="mb-0">
                                                {member.first_name} {member.last_name}
                                                {member.user_id === group?.created_by && (
                                                    <FontAwesomeIcon 
                                                        icon={faCrown} 
                                                        className="text-warning ms-2" 
                                                        title="Group Creator"
                                                    />
                                                )}
                                            </h6>
                                            <p className="text-muted small mb-1">@{member.username}</p>
                                        </div>
                                        <div className="member-role">
                                            <span className={`role-badge ${
                                                member.role === 'admin' ? 'admin' : 
                                                member.role === 'therapist' ? 'therapist' : 'member'
                                            }`}>
                                                {member.role === 'therapist' ? (
                                                    <>
                                                        <FontAwesomeIcon icon={faUserMd} className="me-1" />
                                                        Therapist
                                                    </>
                                                ) : (
                                                    member.role
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Follow/Unfollow Button */}
                                    {!isCurrentUser && (
                                        <div className="member-follow">
                                            <button
                                                className={`btn btn-sm ${isFollowing ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                                                onClick={() => toggleFollow(member)}
                                                title={isFollowing ? 'Unfollow' : 'Follow'}
                                            >
                                                <FontAwesomeIcon 
                                                    icon={isFollowing ? faUserCheck : faUserPlus} 
                                                    className="me-1" 
                                                />
                                                {isFollowing ? 'Following' : 'Follow'}
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Actions Dropdown */}
                                    {(canRemoveMember || canPromoteToAdmin || canTransferOwnership) && (
                                        <div className="member-actions">
                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                                    type="button"
                                                    onClick={() => setActiveDropdown(activeDropdown === member.user_id ? null : member.user_id)}
                                                    aria-expanded={activeDropdown === member.user_id}
                                                >
                                                    <FontAwesomeIcon icon={faEllipsisVertical} />
                                                </button>
                                                
                                                {activeDropdown === member.user_id && (
                                                    <div className="dropdown-menu show">
                                                        {canPromoteToAdmin && (
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => updateMemberRole(member, 'admin')}
                                                                disabled={loading}
                                                            >
                                                                <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                                                                Promote to Admin
                                                            </button>
                                                        )}
                                                        
                                                        {canRemoveMember && (
                                                            <button
                                                                className="dropdown-item text-danger"
                                                                onClick={() => removeGroupMember(member)}
                                                                disabled={loading}
                                                            >
                                                                <FontAwesomeIcon icon={faUserMinus} className="me-2" />
                                                                Remove Member
                                                            </button>
                                                        )}
                                                        
                                                        {canTransferOwnership && (
                                                            <button
                                                                className="dropdown-item text-warning"
                                                                onClick={() => transferOwnership(member)}
                                                                disabled={loading}
                                                            >
                                                                <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                                                                Transfer Ownership
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Summary */}
                <div className="summary-section">
                    <div className="row">
                        <div className="col-md-3">
                            <div className="summary-item">
                                <h5 className="text-primary">{members.length}</h5>
                                <small>Total Members</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="summary-item">
                                <h5 className="text-danger">
                                    {members.filter(m => m.role === 'admin').length}
                                </h5>
                                <small>Admins</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="summary-item">
                                <h5 className="text-success">
                                    {members.filter(m => m.role === 'therapist').length}
                                </h5>
                                <small>Therapists</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="summary-item">
                                <h5 className="text-secondary">
                                    {members.filter(m => m.role === 'member').length}
                                </h5>
                                <small>Members</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />

            {/* Add Member Modal */}
            {showAddModal && (
                <AddMemberModal 
                    groupId={groupId}
                    onClose={() => setShowAddModal(false)}
                    onMemberAdded={loadMembers}
                />
            )}

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Add Member Modal Component
const AddMemberModal = ({ groupId, onClose, onMemberAdded }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [addingUsers, setAddingUsers] = useState(new Set());

    // Debounced search
    useEffect(() => {
        if (searchQuery.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        const timeoutId = setTimeout(async () => {
            await searchUsers(searchQuery.trim());
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const searchUsers = async (query) => {
        try {
            setSearching(true);
            setError(null);

            const response = await fetch(`${backendUrl}/search_users.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    search_query: query,
                    group_id: groupId,
                    current_user_id: user.user_id
                })
            });

            const data = await response.json();

            if (data.success) {
                setSearchResults(data.data || []);
            } else {
                setError(data.messages || 'Failed to search users');
                setSearchResults([]);
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error searching users:', err);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    const addUserToGroup = async (userToAdd) => {
        try {
            setAddingUsers(prev => new Set(prev).add(userToAdd.user_id));
            setError(null);
            setSuccess(null);

            console.log('Adding user to group:', userToAdd); // Debug log

            const response = await fetch(`${backendUrl}/invite_to_group.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    invited_by: user.user_id,
                    invitation_type: 'username',
                    invitation_value: userToAdd.username
                })
            });

            const data = await response.json();
            console.log('Add user response:', data); // Debug log

            if (data.success) {
                setSuccess(`${userToAdd.username} has been added successfully`);
                setSearchResults(prev => prev.filter(u => u.user_id !== userToAdd.user_id));
                
                // Refresh the member list with a small delay to ensure DB is updated
                if (onMemberAdded) {
                    console.log('Refreshing member list...'); // Debug log
                    setTimeout(async () => {
                        await onMemberAdded();
                    }, 500); // 500ms delay
                }
                
                // Auto-dismiss success message after 3 seconds
                setTimeout(() => {
                    setSuccess(null);
                }, 3000);
            } else {
                setError(data.messages || `Failed to add ${userToAdd.username}`);
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error adding user:', err);
        } finally {
            setAddingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(userToAdd.user_id);
                return newSet;
            });
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add Members</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        
                        {success && (
                            <div className="alert alert-success d-flex justify-content-between align-items-center" role="alert">
                                <span>{success}</span>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setSuccess(null)}
                                    aria-label="Close"
                                ></button>
                            </div>
                        )}

                        <div className="mb-3">
                            <label htmlFor="searchInput" className="form-label">Search by username</label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="searchInput"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Type username to search..."
                                    autoComplete="off"
                                />
                                {searching && (
                                    <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                        <div className="spinner-border spinner-border-sm text-muted" role="status">
                                            <span className="visually-hidden">Searching...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="form-text">
                                Start typing a username to find users to add to the group
                            </div>
                        </div>

                        {/* Search Results */}
                        {searchQuery.trim().length >= 2 && (
                            <div className="search-results">
                                <h6 className="text-muted mb-3">Search Results</h6>
                                {searchResults.length > 0 ? (
                                    <div className="list-group">
                                        {searchResults.map((searchUser) => (
                                            <div key={searchUser.user_id} className="list-group-item">
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3">
                                                        {searchUser.photo_url ? (
                                                            <img 
                                                                src={searchUser.photo_url} 
                                                                alt={searchUser.display_name}
                                                                className="rounded-circle"
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                                                 style={{ width: '50px', height: '50px', fontSize: '18px' }}>
                                                                {searchUser.first_name ? searchUser.first_name.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="fw-bold">{searchUser.display_name}</div>
                                                        <div className="text-muted">@{searchUser.username}</div>
                                                    </div>
                                                    <div>
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() => addUserToGroup(searchUser)}
                                                            disabled={addingUsers.has(searchUser.user_id)}
                                                        >
                                                            {addingUsers.has(searchUser.user_id) ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                                    Adding...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                                                                    Add
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-4">
                                        <FontAwesomeIcon icon={faUsers} size="2x" className="mb-2" />
                                        <p>No users found matching "{searchQuery}"</p>
                                        <small>Try a different username</small>
                                    </div>
                                )}
                            </div>
                        )}

                        {searchQuery.trim().length < 2 && (
                            <div className="text-center text-muted py-4">
                                <FontAwesomeIcon icon={faUsers} size="2x" className="mb-2" />
                                <p>Start typing a username to search for users</p>
                                <small>Minimum 2 characters required</small>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

export default GroupMembers;
